/**
 * Knowledge Engine text extraction — normalize -> sectionize -> hash.
 *
 * Normalization runs exactly once, before sectioning and hashing, and every
 * downstream char offset (section, then chunk) is relative to its output.
 * Sections are built by slicing the normalized text at real indices, never
 * by joining or re-rendering strings, so `fullText.slice(charStart,
 * charStart + text.length) === text` holds by construction for every
 * section — the same offset invariant `chunkDocument` depends on.
 */

import type { KnowledgeDocumentSection } from './chunk-document';

export type KnowledgeSourceType = 'upload' | 'url' | 'transcript' | 'paste' | 'image';

export interface ExtractedDocument {
  fullText: string;
  sections: KnowledgeDocumentSection[];
  contentHash: string;
  charCount: number;
}

const UNICODE_SPACE_PATTERN = /[\u00A0\u1680\u180E\u2000-\u200A\u202F\u205F\u3000]/g;
const ZERO_WIDTH_PATTERN = /[\u200B-\u200D\uFEFF]/g;
const ATX_HEADING_PATTERN = /^(#{1,6})(?:\s+(.*?))?\s*$/;
const OPENING_FENCE_PATTERN = /^(`{3,})/;

export function normalizeText(raw: string): string {
  // NUL: Postgres text columns reject it, so a file carrying one would fail
  // at insert with an error no person can act on.
  let text = raw
    .replace(/\u0000/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  text = text.normalize('NFC');
  text = text.replace(UNICODE_SPACE_PATTERN, ' ');
  text = text.replace(ZERO_WIDTH_PATTERN, '');
  text = text.replace(/[ \t]+$/gm, '');
  text = text.replace(/\n{3,}/g, '\n\n');
  return text;
}

/**
 * Strips markdown link and image plumbing, keeping the human-readable text.
 *
 * Scraped pages arrive as markdown, and storing it raw was measurably bad: a
 * real Wikipedia ingest produced chunks whose text was mostly URLs and
 * `[![Image 14](https://upload.wikimedia.org/...)]` — noise that pollutes the
 * embedding, spends chunk budget on link targets, and renders as garbage in
 * any excerpt shown beside a citation. The pipeline completed; the output was
 * unusable, which is a failure, not a pass.
 *
 * Applied only to scraped/markdown sources — a pasted document is left alone,
 * since its brackets are probably its own.
 */
export function stripMarkdownArtifacts(raw: string): string {
  // The scraper prepends its own header block. Left in, it becomes the opening
  // of chunk 0 — so the first thing retrieval can quote back is scraper
  // boilerplate rather than the article.
  // Matched loosely on purpose: the header arrives space-separated on ONE line
  // as often as it does across several, so a newline-anchored pattern silently
  // never fires (it didn't, against real scraped output). Bounded to the first
  // 500 characters and requiring both markers, so ordinary prose is untouched.
  let text = raw.replace(/^\s*Title:[\s\S]{0,500}?Markdown Content:\s*/, '');
  // Citation chips first (`[[164]](url)`), before the generic link rule turns
  // them into a bare `[164]`.
  text = text.replace(/\[\[\d+\]\]\([^)]*\)/g, '');
  // Images, including a link-wrapped image: the alt text is rarely content.
  text = text.replace(/\[?!\[[^\]]*\]\([^)]*\)\]?(\([^)]*\))?/g, '');
  // Inline links: keep the label, drop the target and any title attribute.
  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
  // Bare autolinks and leftover naked URLs on their own.
  text = text.replace(/<https?:\/\/[^>]*>/g, '');
  text = text.replace(/^\s*https?:\/\/\S+\s*$/gm, '');
  // Collapse the whitespace the removals leave behind.
  text = text.replace(/[ \t]{2,}/g, ' ');
  return dropTrailingReferenceSections(text);
}

/*
 * NOT anchored to a line start, deliberately. Scraped output arrives largely
 * on ONE line, so `## References` sits mid-line — a `^...$` pattern matches
 * nothing and looks correct while doing it. That exact mistake was made twice
 * here already (the scraper header, then this); the `##` prefix is what
 * distinguishes a heading from prose that merely says "references".
 */
const REFERENCE_HEADINGS =
  /#{2,6}\s*(references|external links|further reading|see also|citations|notes|bibliography|sources)\b/gi;

/**
 * Cuts a scraped article's trailing reference apparatus.
 *
 * A reference list is a wall of headlines. Retrieval happily returns one, and
 * the model then quotes a citation TITLE as if it were the article's own
 * claim — live example: an angle whose entire evidence was "Georgia
 * Legislature Suspends Tax Exemption for Data Centers". Verbatim, so the
 * anti-hallucination check passes it; useless as something to show a reader,
 * because the source never said it — it merely linked to someone who did.
 *
 * Only fires when the heading sits in the last 40% of the document, so an
 * article that discusses "Sources" early keeps its body.
 */
/*
 * A citation RUN, for pages whose reference block carries no heading at all.
 *
 * Real example: the scraped "Data center" article ends in
 * `188. ↑"..." _Upjohn_. Archived from the original on April 28, 2026.` with no
 * `## References` anywhere — so the heading rule below saw nothing and the
 * block stayed, and retrieval quoted "Georgia Legislature Suspends Tax
 * Exemption for Data Centers" as if the article had claimed it.
 *
 * Two independent hallmarks, either of which is enough when repeated: the
 * back-reference arrow Wikipedia puts on every citation, and the archive
 * boilerplate. Ordinary prose does not repeat either.
 */
const CITATION_RUN_MARKERS: readonly RegExp[] = [/\d+\.\s*↑/g, /Archived from the original/gi];
const CITATION_RUN_MIN_HITS = 5;

function findCitationRunStart(text: string, floor: number): number {
  let earliest = -1;
  for (const marker of CITATION_RUN_MARKERS) {
    marker.lastIndex = 0;
    const hits = [...text.matchAll(marker)]
      .map((m) => m.index)
      .filter((i): i is number => i !== undefined);
    if (hits.length < CITATION_RUN_MIN_HITS) continue;
    // Cut at the FIRST hit of a run that is genuinely trailing, not at a lone
    // early mention.
    const trailing = hits.filter((i) => i >= floor);
    if (trailing.length < CITATION_RUN_MIN_HITS) continue;
    // The run is confirmed by its trailing density, but the cut belongs at
    // where the run BEGINS — cutting at the first hit past the floor leaves
    // the opening citations behind, headline and all, which is exactly the
    // text that was getting quoted. A run may start slightly before the
    // floor; 40% is the earliest it is allowed to reach back.
    const runFloor = text.length * 0.4;
    const start = hits.find((i) => i >= runFloor) ?? trailing[0]!;
    if (earliest < 0 || start < earliest) earliest = start;
  }
  return earliest;
}

export function dropTrailingReferenceSections(text: string): string {
  const floor = text.length * 0.6;
  // The EARLIEST footer heading past the floor: "Further reading" routinely
  // precedes "References", and cutting at the later one leaves a block of
  // book titles behind — which is exactly the junk that got quoted as evidence.
  let cutAt = -1;
  REFERENCE_HEADINGS.lastIndex = 0;
  for (const match of text.matchAll(REFERENCE_HEADINGS)) {
    if (match.index === undefined || match.index < floor) continue;
    cutAt = match.index;
    break;
  }
  const citationStart = findCitationRunStart(text, floor);
  if (citationStart >= 0 && (cutAt < 0 || citationStart < cutAt)) {
    cutAt = citationStart;
  }

  if (cutAt < 0) return text;
  return text.slice(0, cutAt).trimEnd();
}

export async function hashContent(text: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

interface Line {
  text: string;
  start: number;
  end: number;
}

function splitLines(text: string): Line[] {
  const lines: Line[] = [];
  let start = 0;
  for (let i = 0; i <= text.length; i++) {
    if (i === text.length || text[i] === '\n') {
      lines.push({ text: text.slice(start, i), start, end: i });
      start = i + 1;
    }
  }
  return lines;
}

function isTableLine(line: string): boolean {
  return line.includes('|') && line.trim().length > 0;
}

function parseHeading(line: string): { level: number; text: string } | null {
  const match = ATX_HEADING_PATTERN.exec(line);
  if (!match) return null;
  const text = (match[2] ?? '').replace(/\s+#+\s*$/, '').trim();
  return { level: match[1].length, text };
}

function assertSectionOffsetsResolve(sections: KnowledgeDocumentSection[], fullText: string): void {
  for (const [index, section] of sections.entries()) {
    const resolved = fullText.slice(section.charStart, section.charStart + section.text.length);
    if (resolved !== section.text) {
      throw new Error(
        `sectionize: section ${index} at charStart ${section.charStart} does not resolve against fullText — this section would be uncitable`,
      );
    }
  }
}

export function sectionizeMarkdown(text: string): KnowledgeDocumentSection[] {
  const lines = splitLines(text);
  const sections: KnowledgeDocumentSection[] = [];
  const headingStack: { level: number; text: string }[] = [];

  let contentStart: number | null = null;
  let contentEnd: number | null = null;

  const flush = (): void => {
    if (contentStart === null || contentEnd === null) return;
    const sectionText = text.slice(contentStart, contentEnd);
    if (sectionText.trim().length > 0) {
      sections.push({
        text: sectionText,
        charStart: contentStart,
        headingPath: headingStack.map((heading) => heading.text),
        pageNumber: null,
      });
    }
    contentStart = null;
    contentEnd = null;
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    const fenceMatch = OPENING_FENCE_PATTERN.exec(line.text);
    if (fenceMatch) {
      flush();
      const fenceLength = fenceMatch[1].length;
      const closePattern = new RegExp(`^\`{${fenceLength},}\\s*$`);
      const blockStart = line.start;
      let blockEnd = line.end;
      let j = i + 1;
      for (; j < lines.length; j++) {
        blockEnd = lines[j].end;
        if (closePattern.test(lines[j].text)) {
          j++;
          break;
        }
      }
      sections.push({
        text: text.slice(blockStart, blockEnd),
        charStart: blockStart,
        headingPath: headingStack.map((heading) => heading.text),
        pageNumber: null,
        isAtomic: true,
      });
      i = j;
      continue;
    }

    const heading = parseHeading(line.text);
    if (heading) {
      flush();
      while (
        headingStack.length > 0 &&
        headingStack[headingStack.length - 1].level >= heading.level
      ) {
        headingStack.pop();
      }
      headingStack.push(heading);
      i++;
      continue;
    }

    if (isTableLine(line.text)) {
      flush();
      const blockStart = line.start;
      let blockEnd = line.end;
      let j = i + 1;
      while (j < lines.length && isTableLine(lines[j].text)) {
        blockEnd = lines[j].end;
        j++;
      }
      sections.push({
        text: text.slice(blockStart, blockEnd),
        charStart: blockStart,
        headingPath: headingStack.map((heading) => heading.text),
        pageNumber: null,
        isAtomic: true,
      });
      i = j;
      continue;
    }

    if (contentStart === null) contentStart = line.start;
    contentEnd = line.end;
    i++;
  }
  flush();

  assertSectionOffsetsResolve(sections, text);
  return sections;
}

export function sectionizePlainText(text: string): KnowledgeDocumentSection[] {
  const lines = splitLines(text);
  const sections: KnowledgeDocumentSection[] = [];

  let contentStart: number | null = null;
  let contentEnd: number | null = null;

  const flush = (): void => {
    if (contentStart === null || contentEnd === null) return;
    const sectionText = text.slice(contentStart, contentEnd);
    if (sectionText.trim().length > 0) {
      sections.push({
        text: sectionText,
        charStart: contentStart,
        headingPath: [],
        pageNumber: null,
      });
    }
    contentStart = null;
    contentEnd = null;
  };

  for (const line of lines) {
    if (line.text.trim().length === 0) {
      flush();
      continue;
    }
    if (contentStart === null) contentStart = line.start;
    contentEnd = line.end;
  }
  flush();

  assertSectionOffsetsResolve(sections, text);
  return sections;
}

function containsAtxHeading(text: string): boolean {
  return splitLines(text).some((line) => parseHeading(line.text) !== null);
}

export async function extractFromText(
  raw: string,
  kind: 'paste' | 'transcript' | 'url',
): Promise<ExtractedDocument> {
  // Scraped pages arrive as markdown; strip the link plumbing BEFORE
  // normalizing so every offset is measured against the text we actually
  // store. A paste keeps its brackets — they are probably the author's.
  const source = kind === 'url' ? stripMarkdownArtifacts(raw) : raw;
  const fullText = normalizeText(source);
  const sections = containsAtxHeading(fullText)
    ? sectionizeMarkdown(fullText)
    : sectionizePlainText(fullText);
  const contentHash = await hashContent(fullText);
  return { fullText, sections, contentHash, charCount: fullText.length };
}
